import { useState } from 'react';
import { Image as ImageIcon } from 'lucide-react';

const ImageWithFallback = ({ 
  src, 
  alt = 'Image', 
  className = '', 
  fallbackType = 'default',
  ...props 
}) => {
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);

  // Different placeholder SVGs for different contexts
  const getPlaceholderSVG = (type) => {
    const baseStyle = "width: 100%; height: 100%; object-fit: cover;";
    
    switch(type) {
      case 'blog':
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23e5e7eb' width='400' height='300'/%3E%3Cg transform='translate(200 150)'%3E%3Cpath fill='%239ca3af' d='M-30-20h60v5h-60zM-40-10h80v3h-80zM-35 0h70v3h-70zM-30 10h60v3h-60z'/%3E%3Ccircle fill='%239ca3af' cx='0' cy='-35' r='15'/%3E%3C/g%3E%3Ctext x='200' y='270' text-anchor='middle' fill='%239ca3af' font-family='Arial' font-size='14'%3EBlog Görseli%3C/text%3E%3C/svg%3E`;
      
      case 'project':
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23dbeafe' width='400' height='300'/%3E%3Cg transform='translate(200 150)'%3E%3Crect fill='%233b82f6' x='-40' y='-40' width='80' height='60' rx='5'/%3E%3Crect fill='%2360a5fa' x='-35' y='-25' width='25' height='30' rx='2'/%3E%3Crect fill='%2360a5fa' x='5' y='-25' width='30' height='15' rx='2'/%3E%3Crect fill='%2360a5fa' x='5' y='-5' width='30' height='10' rx='2'/%3E%3C/g%3E%3Ctext x='200' y='270' text-anchor='middle' fill='%233b82f6' font-family='Arial' font-size='14'%3EProje Görseli%3C/text%3E%3C/svg%3E`;
      
      case 'service':
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect fill='%23f3f4f6' width='200' height='200' rx='10'/%3E%3Cg transform='translate(100 100)'%3E%3Ccircle fill='%236b7280' r='30'/%3E%3Cpath fill='%23f3f4f6' d='M-15-5h30v10h-30z'/%3E%3Crect fill='%23f3f4f6' x='-5' y='-15' width='10' height='30'/%3E%3C/g%3E%3Ctext x='100' y='180' text-anchor='middle' fill='%236b7280' font-family='Arial' font-size='12'%3EHizmet İkonu%3C/text%3E%3C/svg%3E`;
      
      case 'certificate':
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 300 200'%3E%3Crect fill='%23fef3c7' width='300' height='200' rx='5'/%3E%3Crect fill='%23fbbf24' x='10' y='10' width='280' height='180' rx='3' stroke='%23f59e0b' stroke-width='2' fill='none'/%3E%3Cg transform='translate(150 80)'%3E%3Cpath fill='%23f59e0b' d='M0-20 L5-5 L20-5 L10 5 L15 20 L0 10 L-15 20 L-10 5 L-20-5 L-5-5 Z'/%3E%3C/g%3E%3Ctext x='150' y='130' text-anchor='middle' fill='%23d97706' font-family='Arial' font-size='14' font-weight='bold'%3ESERTİFİKA%3C/text%3E%3C/svg%3E`;
      
      case 'profile':
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 200 200'%3E%3Crect fill='%23e5e7eb' width='200' height='200'/%3E%3Ccircle fill='%239ca3af' cx='100' cy='80' r='30'/%3E%3Cpath fill='%239ca3af' d='M100 120 C70 120 50 140 50 160 L50 200 L150 200 L150 160 C150 140 130 120 100 120Z'/%3E%3C/svg%3E`;
      
      default:
        return `data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 400 300'%3E%3Crect fill='%23f3f4f6' width='400' height='300'/%3E%3Cg transform='translate(200 150)'%3E%3Crect fill='%239ca3af' x='-40' y='-30' width='80' height='60' rx='5'/%3E%3Ccircle fill='%23d1d5db' cx='-20' cy='-15' r='8'/%3E%3Cpath fill='%23d1d5db' d='M-40 10 L-20 -10 L10 10 L40 -5 L40 30 L-40 30 Z'/%3E%3C/g%3E%3C/svg%3E`;
    }
  };

  const handleError = () => {
    setError(true);
    setLoading(false);
  };

  const handleLoad = () => {
    setLoading(false);
  };

  // If no src or error occurred, show placeholder
  if (!src || error) {
    return (
      <div className={`relative bg-gray-100 flex items-center justify-center ${className}`}>
        <img 
          src={getPlaceholderSVG(fallbackType)}
          alt={alt}
          className={className}
          style={{ objectFit: 'cover' }}
          {...props}
        />
      </div>
    );
  }

  return (
    <>
      {loading && (
        <div className={`absolute inset-0 bg-gray-100 animate-pulse ${className}`} />
      )}
      <img
        src={src}
        alt={alt}
        className={className}
        onError={handleError}
        onLoad={handleLoad}
        {...props}
      />
    </>
  );
};

export default ImageWithFallback;
